import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Divider } from '@mui/material';
import ProductFormStockInventory from 'components/MenuNew/FormView/Inventory/ProductFormStockInventory';
import ProductFormInventory from 'components/MenuNew/FormView/Inventory/ProductFormInventory';
import { InventoryConfigProvider } from 'components/MenuNew/FormView/Inventory/InventoryConfigContext';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as posMenuService from 'services/posMenuService';
import { generatePosItemInventory } from 'redux/actions/posMenu';
import { getBusinessInventoryList } from 'redux/actions/inventory/inventory';
import { selectCurrentBusiness } from 'redux/selectors';
import * as types from 'redux/actions/types';

// Wires the shared (regular menu) inventory tab UI to POS menu items.
// The UI/logic is reused verbatim; only the entity-refetch and the
// "generate inventory" create flow target POS-specific endpoints.
const ItemInventory = ({ item }) => {
  const dispatch = useDispatch();
  const currentBusiness = useSelector(selectCurrentBusiness);
  const itemId = item?._id;
  const [generateInventoryLoading, setGenerateInventoryLoading] =
    useState(false);

  const buildFormValues = useCallback(
    (source) => ({
      _id: source?._id || itemId,
      inventory: source?.inventory || [],
      useInventory: source?.useInventory ?? false,
    }),
    [itemId]
  );

  const productFormMethods = useForm({
    defaultValues: buildFormValues(item),
    mode: 'all',
  });

  useEffect(() => {
    productFormMethods.reset(buildFormValues(item));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, item?.inventory, item?.useInventory]);

  // POS items live in a different collection than regular menu products, so
  // refetch through the POS item endpoint and keep the menu map in sync.
  const refetchProduct = useCallback(
    async (id) => {
      const fresh = await posMenuService.getItemById(id);
      if (fresh) {
        dispatch({ type: types.POS_MENU_UPDATE_ITEM, payload: fresh });
      }
      return buildFormValues(fresh);
    },
    [dispatch, buildFormValues]
  );

  // POS items don't live in orders.co-mms, so the shared inventory tab's
  // add/replace/delete must target the POS item (pos-mms) instead of the MMS
  // `/products/:id/inventory` endpoints. We patch the item's `inventory` array
  // via cloud-pos (pos-mms does a true partial merge).
  const addInventory = useCallback(
    async ({ uuid, qty, isMain = false }) => {
      const current = productFormMethods.getValues('inventory') || [];
      const inventory = [...current, { uuid, qty, isMain }];
      return posMenuService.updateItem(itemId, { inventory });
    },
    [itemId, productFormMethods]
  );

  const replaceInventory = useCallback(
    async ({ uuid, qty, isMain }, oldUuid) => {
      const current = productFormMethods.getValues('inventory') || [];
      const inventory = current.map((entry) =>
        entry.uuid === oldUuid
          ? { ...entry, uuid, qty, isMain: isMain ?? entry.isMain }
          : entry
      );
      return posMenuService.updateItem(itemId, { inventory });
    },
    [itemId, productFormMethods]
  );

  const deleteInventory = useCallback(
    async (uuid) => {
      const current = productFormMethods.getValues('inventory') || [];
      const inventory = current.filter((entry) => entry.uuid !== uuid);
      return posMenuService.updateItem(itemId, { inventory });
    },
    [itemId, productFormMethods]
  );

  const inventoryConfig = useMemo(
    () => ({ refetchProduct, addInventory, replaceInventory, deleteInventory }),
    [refetchProduct, addInventory, replaceInventory, deleteInventory]
  );

  const handleGenerateInventory = useCallback(async () => {
    const itemUuid = item?.uuid;
    if (!itemUuid) return;
    setGenerateInventoryLoading(true);
    try {
      await dispatch(
        generatePosItemInventory(itemUuid, currentBusiness?.uuid)
      );
      const fresh = await refetchProduct(itemId);
      if (fresh) {
        productFormMethods.reset(fresh);
      }
      if (currentBusiness?.uuid) {
        await dispatch(getBusinessInventoryList(currentBusiness.uuid));
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      console.error(
        `Add Inventory failed for POS item ${itemUuid}, business ${currentBusiness?.uuid}: ${message}`,
        error
      );
      toast.error(`Failed to add inventory: ${message}`);
    } finally {
      setGenerateInventoryLoading(false);
    }
  }, [
    item?.uuid,
    itemId,
    currentBusiness?.uuid,
    dispatch,
    refetchProduct,
    productFormMethods,
  ]);

  if (!itemId) return null;

  return (
    <InventoryConfigProvider value={inventoryConfig}>
      <Box>
        <ProductFormStockInventory
          productFormMethods={productFormMethods}
          onAddInventory={handleGenerateInventory}
          isGenerateInventoryLoading={generateInventoryLoading}
        />
        {productFormMethods.getValues('inventory')?.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <ProductFormInventory productFormMethods={productFormMethods} />
          </>
        )}
      </Box>
    </InventoryConfigProvider>
  );
};

export default ItemInventory;
